import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  CameraAlt as CameraAltIcon,
  Cameraswitch as CameraswitchIcon,
  CheckCircle as CheckCircleIcon,
  Replay as ReplayIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material/';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { AppContext } from '../../context/Context';
import { SocketContext } from '../../context/SocketContext';
import '../../assets/css/buttons.css';

// Shared camera preview/capture/upload; mount only while the camera is on (shared upload channel)
function ReceiptCapture({ title, help, filePrefix, onDone, onCancel }) {
  const { t } = useTranslation();
  const { socket } = useContext(SocketContext);
  const { hall, operator, session } = useContext(AppContext);

  const [captured, setCaptured] = useState(null);
  const [busy, setBusy] = useState(false);
  const [devices, setDevices] = useState([]);
  const [cameraIndex, setCameraIndex] = useState(0);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  useEffect(() => {
    if (!socket) return;

    const handleUpload = (response) => {
      setBusy(false);
      if (!response?.url) {
        toast.error(t('errorTicket.photoFailed'));
        return;
      }
      toast.success(t('errorTicket.photoAttached'));
      onDone(response.url);
    };

    socket.on('uploadImageSignature', handleUpload);
    return () => socket.off('uploadImageSignature', handleUpload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  useEffect(() => {
    if (captured) {
      stopCamera();
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const first = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
        // Labels only arrive once permission is granted, so enumerate after asking
        const all = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = all.filter((d) => d.kind === 'videoinput');

        if (cancelled) {
          first.getTracks().forEach((track) => track.stop());
          return;
        }
        setDevices(videoDevices);

        // The first stream is whatever the browser picked; swap if another was chosen
        let live = first;
        const wanted = videoDevices[cameraIndex]?.deviceId;
        if (wanted && cameraIndex > 0) {
          first.getTracks().forEach((track) => track.stop());
          live = await navigator.mediaDevices.getUserMedia({
            video: { deviceId: { exact: wanted } },
          });
        }

        streamRef.current = live;
        if (videoRef.current) {
          videoRef.current.srcObject = live;
          videoRef.current.play();
        }
      } catch (err) {
        console.error('Error accessing the camera:', err);
        toast.error(t('errorTicket.cameraFailed'));
      }
    })();

    return () => {
      cancelled = true;
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captured, cameraIndex]);

  // Closed mid-shot must not leave the camera light on
  useEffect(() => stopCamera, []);

  const handleCapture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    setCaptured(canvas.toDataURL('image/png'));
  };

  const handleUsePhoto = () => {
    if (!captured) return;

    const binary = atob(captured.split(',')[1]);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

    setBusy(true);
    socket.emit('uploadImageSignature', {
      fileBuffer: bytes,
      fileName: `${filePrefix}_${hall}_${Date.now()}.png`,
      bingoName: hall,
      operator,
      session,
    });
  };

  return (
    <Box sx={{ mt: 1, mb: 1, textAlign: 'center' }}>
      <Typography sx={{ color: '#e6eaf2', fontSize: '24px' }}>
        {title}
      </Typography>
      {help && (
        <Typography sx={{ color: '#8fa3d4', fontSize: '15px', mt: 0.5, mb: 1 }}>
          {help}
        </Typography>
      )}

      <Box
        sx={{
          borderRadius: '10px',
          overflow: 'hidden',
          backgroundColor: '#0b1229',
          width: '100%',
          aspectRatio: '4 / 3',
          maxHeight: '58vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {captured ? (
          <img
            src={captured}
            alt=""
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
            }}
          />
        ) : (
          <video
            ref={videoRef}
            muted
            playsInline
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
            }}
          />
        )}
      </Box>

      {/* All three at camera size (180px/16px) so they sit level */}
      <Box
        sx={{
          display: 'flex',
          gap: '12px',
          mt: 2,
          justifyContent: 'center',
          alignItems: 'center',
          flexWrap: 'wrap',
          '& button': { marginTop: 0 },
        }}
      >
        {captured ? (
          <>
            <button
              className="btn-custom-camara"
              onClick={handleUsePhoto}
              disabled={busy}
            >
              <CheckCircleIcon style={{ fontSize: '1.2em' }} />
              {t('errorTicket.photoUse')}
            </button>
            <button
              className="btn-custom-camara-back"
              onClick={() => setCaptured(null)}
            >
              <ReplayIcon style={{ fontSize: '1.2em' }} />
              {t('errorTicket.photoRetake')}
            </button>
          </>
        ) : (
          <>
            <button className="btn-custom-camara" onClick={handleCapture}>
              <CameraAltIcon style={{ fontSize: '1.2em' }} />
              {t('errorTicket.photoTake')}
            </button>
            {devices.length > 1 && (
              <button
                className="btn-custom-camara-back"
                onClick={() => setCameraIndex((i) => (i + 1) % devices.length)}
              >
                <CameraswitchIcon style={{ fontSize: '1.2em' }} />
                {t('errorTicket.photoSwitch')}
              </button>
            )}
          </>
        )}
        {/* Avbryt, not Tilbake: it drops the photo, it does not step back */}
        <button className="btn-custom-camara-cancel" onClick={onCancel}>
          <CancelIcon style={{ fontSize: '1.2em' }} />
          {t('sales.cancel')}
        </button>
      </Box>
    </Box>
  );
}

export default ReceiptCapture;
